import { useContext } from 'react';
import isEmpty from 'lodash/isEmpty';
import { i18nContext } from '../context/i18n.context';
import { stepContext } from '../context/step.context';
import { loggingContext } from '../context/logging.context';
import { UserContext } from '../context/user.context';
import { modalContext } from '@/context/modal.context';
import { STATUS_STARTED, STATUS_UNSTARTED, ASSESSMENT_CHECK_DATA_FIELDS, isEmptyObj } from '../util';

/**
 * Encapsulates the logic for starting (or resuming) an assessment.
 *
 * Returns `startAssessment(handleNext, options)` where `handleNext` is
 * called as `handleNext(lastCompletedStep, answersToApply, lastAnswer)` —
 * i.e. it maps to `resetAndBegin` in AssessmentContent.
 */
const useStartAssessment = () => {
  const { t } = useContext(i18nContext);
  const { api, answers, fetchedAnswers, sequence, assessment, status, resetAnswers, checkData, invitation, setUserAssessmentId, setHistory } =
    useContext(stepContext);
  const { user, agencyClient } = useContext(UserContext);
  const { trackAssessmentStarted, trackAssessmentStatusMarked, alert } = useContext(loggingContext);
  const { enqueueModal } = useContext(modalContext);

  const forceSignin = () => {
    //force to not dismiss
    enqueueModal({ name: 'SignupModal', reason: 'signupBeforeAssessment', preventClose: true }, { forceOpen: true });
  };

  const findMissingPreconditions = () => {
    const errors = [];
    if (!agencyClient && isEmptyObj(user)) {
      const missing = ASSESSMENT_CHECK_DATA_FIELDS.find((attr) => !checkData[attr]);
      // We will force authentication in this case
      if (missing) forceSignin();
    }
    return errors;
  };

  const checkOKToProceed = () => {
    const errors = findMissingPreconditions();
    if (errors.length) {
      alert.errorMessage(errors[0]);
      return false;
    }
    return true;
  };

  const startAssessment = async (handleNext, { resetBeforeStarting } = {}) => {
    if (!checkOKToProceed()) return;

    let lastCompletedStep = false,
      answersToApply = {},
      lastAnswer,
      history = [];

    trackAssessmentStarted({ agencyClient, assessmentId: assessment.id });

    if (status === STATUS_UNSTARTED) {
      const res = await api.markAssessmentStarted(assessment.id, agencyClient?.id, invitation);
      if (res?.data?.user_assessment_id) {
        setUserAssessmentId(res.data.user_assessment_id);
      }
      trackAssessmentStatusMarked({ assessmentId: assessment.id, status: STATUS_STARTED });
    }

    if (resetBeforeStarting) {
      await resetAnswers();
    }

    if (status === STATUS_STARTED && isEmpty(answers)) {
      for (let i = 0; i < sequence.length; i++) {
        if (sequence[i].type !== 'question') continue;
        if (fetchedAnswers[sequence[i].value]) {
          lastAnswer = fetchedAnswers[sequence[i].value].value;
          answersToApply[sequence[i].value] = {
            value: lastAnswer,
            question_id: sequence[i].value,
            step: i
          };
          lastCompletedStep = i;
          history.push(i);
        }
      }
      setHistory([...history]);
    }

    lastCompletedStep = lastCompletedStep === 0 ? false : lastCompletedStep;
    handleNext(lastCompletedStep, answersToApply, lastAnswer);
  };

  return startAssessment;
};

export default useStartAssessment;
